import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChevronRight, Pencil, Plus, Trash2 } from 'lucide-react';

import { useAppDispatch, useAppSelector } from '../../hooks';
import { catalogueReducer } from '../../store/catalogue/catalogue.reducer';
import {
  getCatalogueIsLoading,
  getSelectedManufacturer,
  getSelectedSeries,
} from '../../store/catalogue/catalogue.selectors';
import { LoadingSpinner } from '../loading/LoadingSpinner';
import AddSeriesModal from './AddSeriesModal';
import DeleteConfirmModal from './DeleteConfirmModal';

const { selectSeries, createSeries, modifySeries, removeSeries } = catalogueReducer.actions;

interface EditSeriesData {
  id: string;
  name: string;
  description?: string;
}

interface CatalogueSeriesSidebarProps {
  canEdit?: boolean;
}

const CatalogueSeriesSidebar: React.FC<CatalogueSeriesSidebarProps> = ({ canEdit = true }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const selectedManufacturer = useAppSelector(getSelectedManufacturer);
  const selectedSeries = useAppSelector(getSelectedSeries);
  const isLoading = useAppSelector(getCatalogueIsLoading);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [hoveredSeriesId, setHoveredSeriesId] = useState<string | null>(null);
  const [editSeries, setEditSeries] = useState<EditSeriesData | null>(null);
  const [deletingSeriesData, setDeletingSeriesData] = useState<{ id: string; name: string } | null>(null);

  if (!selectedManufacturer) {
    return null;
  }

  const handleSeriesClick = (seriesId: string) => {
    dispatch(selectSeries(selectedSeries?.id === seriesId ? null : seriesId));
  };

  const handleAddSeries = (name: string, description?: string) => {
    dispatch(createSeries({ manufacturerId: selectedManufacturer.id, name, description }));
  };

  const handleEditSeries = (e: React.MouseEvent, series: EditSeriesData) => {
    e.stopPropagation();
    setEditSeries(series);
  };

  const handleDeleteSeries = (e: React.MouseEvent, series: { id: string; name: string }) => {
    e.stopPropagation();
    setDeletingSeriesData(series);
  };

  const handleUpdateSeries = (name: string, description?: string) => {
    if (editSeries) {
      dispatch(modifySeries({ id: editSeries.id, data: { name, description } }));
      setEditSeries(null);
    }
  };

  const handleConfirmDelete = () => {
    if (deletingSeriesData) {
      dispatch(removeSeries(deletingSeriesData.id));
    }
  };

  return (
    <div
      className="flex flex-col flex-1 min-h-0 bg-black border-r border-zinc-800"
      data-testid="catalogue-series-sidebar"
    >
      {/* Series Header with Add Button */}
      <div
        className="flex items-center justify-between w-full px-4 text-zinc-500 text-sm border-b border-zinc-800"
        style={{
          height: '45px',
        }}
      >
        <span>{t('catalogue.seriesLines')}</span>
        {canEdit && (
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="p-1 hover:bg-greyP4 rounded text-green01 hover:text-green03 transition-colors"
            title={t('catalogue.addSeries')}
          >
            <Plus className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Series List */}
      <div
        className="flex flex-col items-start px-2 py-2 flex-1 overflow-y-auto"
        style={{
          gap: '2px',
        }}
      >
        {isLoading ? (
          <div className="flex justify-center items-center w-full py-8">
            <LoadingSpinner size={24} color="#B3EC49" />
          </div>
        ) : (
          <>
            {selectedManufacturer.series.map((series) => (
              <div
                key={series.id}
                className="relative w-full"
                onMouseEnter={() => setHoveredSeriesId(series.id)}
                onMouseLeave={() => setHoveredSeriesId(null)}
              >
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSeriesClick(series.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSeriesClick(series.id);
                    }
                  }}
                  className={`flex items-center w-full text-sm transition-colors duration-200 h-[40px] cursor-pointer
                ${selectedSeries?.id === series.id ? 'text-white' : 'text-greyP2 hover:text-white hover:bg-greyP4'}`}
                  style={
                    selectedSeries?.id === series.id
                      ? {
                          background: '#232323',
                          borderRadius: '6px',
                          padding: '10px 18px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          alignSelf: 'stretch',
                        }
                      : {
                          borderRadius: '6px',
                          padding: '10px 18px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          alignSelf: 'stretch',
                        }
                  }
                >
                  <span className="truncate flex-1 text-left" title={series.name}>
                    {series.name}
                  </span>
                  {canEdit && hoveredSeriesId === series.id && (
                    <div className="flex items-center gap-1 mr-1">
                      <button
                        onClick={(e) => handleEditSeries(e, series)}
                        className="p-1 hover:bg-white/10 rounded"
                        title={t('common.edit')}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={(e) => handleDeleteSeries(e, series)}
                        className="p-1 hover:bg-white/10 rounded text-red-400"
                        title={t('common.delete')}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                  {series.products.length > 0 && <ChevronRight className="w-4 h-4 flex-shrink-0" />}
                </div>
              </div>
            ))}

            {selectedManufacturer.series.length === 0 && (
              <div className="px-4 py-8 text-center w-full flex flex-col items-center gap-4">
                <p className="text-greyP3 text-sm">{t('catalogue.noSeries')}</p>
                {canEdit && (
                  <button
                    onClick={() => setIsAddModalOpen(true)}
                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-green01 text-black text-sm font-medium
                  hover:bg-green03 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    {t('catalogue.addSeries')}
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Add / Edit Series Modal */}
      <AddSeriesModal
        isOpen={isAddModalOpen || !!editSeries}
        onClose={() => {
          setIsAddModalOpen(false);
          setEditSeries(null);
        }}
        onAddSeries={editSeries ? handleUpdateSeries : handleAddSeries}
        manufacturerName={selectedManufacturer.name}
        editData={editSeries}
      />

      {/* Delete Series Confirm Modal */}
      <DeleteConfirmModal
        isOpen={!!deletingSeriesData}
        onClose={() => setDeletingSeriesData(null)}
        onConfirm={handleConfirmDelete}
        title={t('catalogue.deleteSeriesTitle')}
        itemName={deletingSeriesData?.name || ''}
        itemType="series"
      />
    </div>
  );
};

export default CatalogueSeriesSidebar;
